/**
 * ViewportScoreboard (Iteration 12 — N8)
 * 
 * Collapsible side panel showing counts per viewport.
 * Uses DataTable (from 0C) and SegmentedControl (from 0C).
 * 
 * LAT-22: During recounting, shows "Updating…" — never silently stale.
 * LAT-23: Classes never sum (mat count and equipment count never added together).
 * 
 * Props:
 *   markers: array of marker objects with count data
 *   loading: boolean
 *   error: string | null
 *   supplyMode: 'supply_only' | 'demand_only' | 'both'
 *   open: boolean
 *   onClose: () => void
 *   updating: boolean (viewport change in progress)
 */

import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';

import DataTable from './DataTable';
import SegmentedControl from './SegmentedControl';
import { formatCount } from '../../utils/countModel';

const DRAWER_WIDTH = 380;

export default function ViewportScoreboard({
  markers = [],
  loading = false,
  error = null,
  supplyMode = 'both',
  open = false,
  onClose,
  updating = false,
}) {
  const theme = useTheme();
  const [filter, setFilter] = useState('both');

  // Filter options gated by supplyMode
  const filterOptions = useMemo(() => {
    const opts = [];
    if (supplyMode === 'supply_only' || supplyMode === 'both') {
      opts.push({ value: 'supply', label: 'Supply' });
    }
    if (supplyMode === 'demand_only' || supplyMode === 'both') {
      opts.push({ value: 'demand', label: 'Demand' });
    }
    if (supplyMode === 'both') {
      opts.push({ value: 'both', label: 'Both' });
    }
    return opts;
  }, [supplyMode]);

  // Compute totals
  const totals = useMemo(() => {
    let supply = 0;
    let demand = 0;
    for (const m of markers) {
      supply += (m.supplyManual || 0);
      demand += (m.demandManual || 0);
    }
    return { supply, demand, net: supply - demand };
  }, [markers]);

  // Table columns
  const columns = useMemo(() => {
    const cols = [
      { key: 'scopeName', label: 'Scope', sortable: true },
      { key: 'label', label: 'Location', sortable: true },
    ];

    if (filter === 'supply' || filter === 'both') {
      cols.push({
        key: 'supplyManual',
        label: 'Supply',
        align: 'right',
        sortable: true,
        render: (val) => formatCount(val || 0),
      });
    }

    if (filter === 'demand' || filter === 'both') {
      cols.push({
        key: 'demandManual',
        label: 'Demand',
        align: 'right',
        sortable: true,
        render: (val) => formatCount(val || 0),
      });
    }

    if (filter === 'both') {
      cols.push({
        key: 'net',
        label: 'Net',
        align: 'right',
        sortable: true,
        render: (_, row) => {
          const net = (row.supplyManual || 0) - (row.demandManual || 0);
          return formatCount(net);
        },
      });
    }

    cols.push({
      key: 'lastEntryAt',
      label: 'Updated',
      render: (val) => {
        if (!val) return '—';
        const d = new Date(val);
        return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      },
    });

    return cols;
  }, [filter]);

  // Table rows from markers
  const rows = useMemo(() => {
    return markers.map((m) => ({
      id: m.id,
      scopeName: m.scopeName || '—',
      label: m.label || '—',
      supplyManual: m.supplyManual || 0,
      demandManual: m.demandManual || 0,
      lastEntryAt: m.lastEntryAt || null,
    }));
  }, [markers]);

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          backgroundColor: theme.palette.background.default,
          borderLeft: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Scoreboard
            </Typography>
            {/* LAT-22: Show updating indicator */}
            {updating && (
              <Typography variant="caption" color="warning.main">
                Updating…
              </Typography>
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider />

        {/* Totals */}
        <Box sx={{ px: 2, py: 1.5, display: 'flex', gap: 3 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Supply</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.supply?.main || theme.palette.info.main }}>
              {formatCount(totals.supply)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Total Demand</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.demand?.main || theme.palette.warning.main }}>
              {formatCount(totals.demand)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Net</Typography>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {formatCount(totals.net)}
            </Typography>
          </Box>
        </Box>

        <Divider />

        {/* Filter */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <SegmentedControl
            options={filterOptions}
            value={filter}
            onChange={setFilter}
            size="small"
            fullWidth
          />
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
          {markers.length === 0 && !loading && !error ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                Pan or zoom to see locations
              </Typography>
            </Box>
          ) : markers.length > 0 && markers.every((m) => (m.supplyManual || 0) === 0 && (m.demandManual || 0) === 0) && !loading && !error ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                No counts recorded yet
              </Typography>
            </Box>
          ) : (
            <DataTable
              columns={columns}
              rows={rows}
              loading={loading}
              error={error}
              emptyMessage="No counts recorded yet"
              noMatchMessage="No locations in this viewport"
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
